import React, { useState } from 'react';
import { View, StyleSheet, Alert } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useAuth } from '../../hooks/useAuth';
import { useFeatureFlag, isFeatureEnabled } from '../../hooks/useFeatureFlags';
import { subscriptionAPI } from '../../api/subscriptions';
import { SubscriptionTierBadge } from './SubscriptionTierBadge';
import { SubscriptionPlan } from '../../types/auth';
import { RootStackParamList } from '../../types/navigation';

interface SubscriptionBannerProps {
  style?: Record<string, unknown>;
  showUpgradeButton?: boolean;
  navigateToPricing?: boolean;
}

export const SubscriptionBanner: React.FC<SubscriptionBannerProps> = ({
  style,
  showUpgradeButton = true,
  navigateToPricing = false,
}) => {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { subscriptionPlan } = useAuth();
  const { data: pricingFlag } = useFeatureFlag('pricing_subscription');
  const paymentsKillSwitchOn = isFeatureEnabled(
    pricingFlag,
    'pricing_subscription'
  );
  const [, setIsLoading] = useState(false);
  // Show upgrade for free, plus, trial, pay-per-scan (not for Pro)
  const isPro = subscriptionPlan === SubscriptionPlan.PRO;
  const effectiveShowUpgradeButton =
    showUpgradeButton && !isPro && !paymentsKillSwitchOn;

  const handleUpgrade = async () => {
    if (navigateToPricing) {
      // Navigate to pricing page instead of showing alert
      navigation.navigate('Pricing');
      return;
    }

    try {
      setIsLoading(true);

      if (
        subscriptionPlan === SubscriptionPlan.FREE ||
        subscriptionPlan === SubscriptionPlan.FREEMIUM_TRIAL
      ) {
        // Upgrade to Pro
        Alert.alert(
          'Upgrade to Pro',
          'Would you like to upgrade to Pro Plan for full access to all features?',
          [
            { text: 'Cancel', style: 'cancel' },
            {
              text: 'Upgrade',
              onPress: async () => {
                try {
                  await subscriptionAPI.upgradeToPro();
                  Alert.alert('Success', 'Upgraded to Pro Plan!');
                } catch (error: unknown) {
                  const err = error as Error;
                  Alert.alert(
                    'Error',
                    err.message || 'Failed to upgrade subscription'
                  );
                }
              },
            },
          ]
        );
      } else {
        // Start trial for pay-per-scan users
        Alert.alert(
          'Start Trial',
          'Would you like to start a 7-day free trial?',
          [
            { text: 'Cancel', style: 'cancel' },
            {
              text: 'Start Trial',
              onPress: async () => {
                try {
                  await subscriptionAPI.startFreemiumTrial();
                  Alert.alert('Success', 'Started 7-day free trial!');
                } catch (error: unknown) {
                  const err = error as Error;
                  Alert.alert('Error', err.message || 'Failed to start trial');
                }
              },
            },
          ]
        );
      }
    } catch (error: unknown) {
      const err = error as Error;
      Alert.alert('Error', err.message || 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <View style={[styles.container, style]}>
      <SubscriptionTierBadge
        subscriptionPlan={subscriptionPlan}
        onUpgrade={effectiveShowUpgradeButton ? handleUpgrade : undefined}
        showUpgradeButton={effectiveShowUpgradeButton}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    // Just a simple container for the subscription badge
  },
});
